import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import FacebookIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/X';
import SitemarkIcon from './SitemarkIcon';
import FooterSitemarkIcon from './FooterSitemarkIcon';

function Copyright() {
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
      {'Copyright © '}
      <Link color="text.secondary" href="https://google.com">
        Womapp
      </Link>
      &nbsp;
      {new Date().getFullYear()}
    </Typography>
  );
}

// PDF Viewer Component
interface PDFViewerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  pdfUrl: string;
}

function PDFViewer({ open, onClose, title, pdfUrl }: PDFViewerProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {pdfUrl ? (
          <>
            <object
              data={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{
                minHeight: '500px',
                flex: 1,
              }}
            >
              <iframe
                src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  minHeight: '500px',
                  flex: 1,
                }}
                title={title}
                onLoad={(e) => {
                  console.log('PDF URL:', pdfUrl);
                  console.log('Iframe loaded');
                }}
                onError={(e) => {
                  console.error('Error loading PDF:', e);
                }}
              />
            </object>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              PDF not available
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {pdfUrl && (
          <Button
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            Open in New Tab
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function Footer() {
  const [pdfViewer, setPdfViewer] = React.useState({
    open: false,
    title: '',
    pdfUrl: '',
  });

  // PDF URLs - if you moved files to public/documents/
  const PDF_URLS: Record<string, string> = {
    privacy: '/assets/documents/privacy-policy.pdf',
    terms: '/assets/documents/terms-of-service.pdf',
  };

  const handleOpenPDF = (type: string, title: string) => {
    setPdfViewer({
      open: true,
      title,
      pdfUrl: PDF_URLS[type],
    });
  };

  const handleClosePDF = () => {
    setPdfViewer({
      open: false,
      title: '',
      pdfUrl: '',
    });
  };

  return (
    <>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 8 },
          py: { xs: 8, sm: 10 },
          textAlign: { sm: 'center', md: 'left' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minWidth: { xs: '100%', sm: '60%' },
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: '60%' } }}>
              <FooterSitemarkIcon />
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Join the newsletter
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Subscribe for weekly updates. No spams ever!
              </Typography>
              <InputLabel htmlFor="email-newsletter">Email</InputLabel>
              <Stack direction="row" spacing={1} useFlexGap>
                <TextField
                  id="email-newsletter"
                  hiddenLabel
                  size="small"
                  variant="outlined"
                  fullWidth
                  aria-label="Enter your email address"
                  placeholder="Your email address"
                  slotProps={{
                    htmlInput: {
                      autoComplete: 'off',
                      'aria-label': 'Enter your email address',
                    },
                  }}
                  sx={{ width: '250px' }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  Subscribe
                </Button>
              </Stack>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Product
            </Typography>
            <Link color="text.secondary" variant="body2" href="#">
             Services
            </Link>
            <Link color="text.secondary" variant="body2" href="#">
              Pricing
            </Link>
            <Link color="text.secondary" variant="body2" href="#">
              FAQs
            </Link>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Company
            </Typography>
            <Link color="text.secondary" variant="body2" href="#">
              About us
            </Link>
            <Link color="text.secondary" variant="body2" href="#">
              Become a Tasker
            </Link>
            <Link color="text.secondary" variant="body2" href="#">
              Press
            </Link>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Legal
            </Typography><Link
              color="text.secondary"
              variant="body2"
              component="button"
              onClick={() => handleOpenPDF('terms', 'Terms of Service')}
              sx={{
                textDecoration: 'underline',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                p: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Terms
            </Link>
            <Link
              color="text.secondary"
              variant="body2"
              component="button"
              onClick={() => handleOpenPDF('privacy', 'Privacy Policy')}
              sx={{
                textDecoration: 'underline',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                p: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Privacy
            </Link>
            <Link color="text.secondary" variant="body2" href="#">
              Contact
            </Link>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 4, sm: 8 },
            width: '100%',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <div>
            <Link
              color="text.secondary"
              variant="body2"
              component="button"
              onClick={() => handleOpenPDF('privacy', 'Privacy Policy')}
              sx={{
                textDecoration: 'underline',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                p: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Privacy Policy
            </Link>
            <Typography sx={{ display: 'inline', mx: 0.5, opacity: 0.5 }}>
              &nbsp;•&nbsp;
            </Typography>
            <Link
              color="text.secondary"
              variant="body2"
              component="button"
              onClick={() => handleOpenPDF('terms', 'Terms of Service')}
              sx={{
                textDecoration: 'underline',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                p: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Terms of Service
            </Link>
            <Copyright />
          </div>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ justifyContent: 'left', color: 'text.secondary' }}
          >
            <IconButton
              color="inherit"
              size="small"
              href="https://github.com"
              aria-label="GitHub"
              sx={{ alignSelf: 'center' }}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              color="inherit"
              size="small"
              href="https://x.com"
              aria-label="X"
              sx={{ alignSelf: 'center' }}
            >
              <TwitterIcon />
            </IconButton>
            <IconButton
              color="inherit"
              size="small"
              href="https://www.linkedin.com"
              aria-label="LinkedIn"
              sx={{ alignSelf: 'center' }}
            >
              <LinkedInIcon />
            </IconButton>
          </Stack>
        </Box>
      </Container>

      {/* PDF Viewer Modal */}
      <PDFViewer
        open={pdfViewer.open}
        onClose={handleClosePDF}
        title={pdfViewer.title}
        pdfUrl={pdfViewer.pdfUrl}
      />
    </>
  );
}